import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiDownload, FiShield, FiArrowLeft, FiShare2 } from 'react-icons/fi';
import api from '../services/api';
import './CertificateVerificationPage.css';
import logo from '../assets/logo.png';

const downloadImage = async (url, filename) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(objectUrl);
};

const CertificateVerificationPage = () => {
  const { verificationCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState('');
  const [shareFeedback, setShareFeedback] = useState('');

  useEffect(() => {
    const fetchCertificate = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.verifyCertificate(verificationCode);
        setCertificate(response.data);
      } catch (err) {
        setError(err?.response?.data?.message || 'Certificate could not be verified');
      } finally {
        setLoading(false);
      }
    };

    if (verificationCode) {
      fetchCertificate();
    }
  }, [verificationCode]);

  const handleShare = async () => {
    if (!certificate) {
      return;
    }

    const shareUrl = certificate.verificationLink || window.location.href;
    setShareFeedback('');

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Verified Certificate',
          text: `${certificate.name}'s internship certificate`,
          url: shareUrl
        });
        setShareFeedback('Certificate URL shared.');
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareFeedback('Share is not available here. Certificate URL copied.');
        return;
      }

      window.prompt('Copy this certificate URL', shareUrl);
      setShareFeedback('Copy the URL and share it.');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setShareFeedback('Unable to share right now. Please try again.');
      }
    }
  };

  return (
    <div className="verification-page">
      <div className="verification-container">
        <a
      href="https://www.dylora.in"
      target="_blank"
      rel="noopener noreferrer"
      className="logo-link"
    >
      <img
        src={logo}
        alt="Dylora Logo"
        className="logo-image"
      />
    </a>

        {loading ? (
          <div className="verification-card">Loading certificate verification...</div>
        ) : error ? (
          <div className="verification-card invalid">
            <h1>Certificate Not Found</h1>
            <p>{error}</p>
          </div>
        ) : (
          <>
            <section className="verification-hero">
              <div className="hero-badge">
                <FiShield /> Verified Certificate
              </div>
              <h1>Certificate Validation Successful</h1>
              <p>This certificate is provided and validated by Dylora.</p>
            </section>

            <section className="verification-card">
              <div className="detail-grid">
                <div>
                  <span>Name</span>
                  <strong>{certificate.name}</strong>
                </div>
                <div>
                  <span>Course</span>
                  <strong>{certificate.course}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{certificate.duration}</strong>
                </div>
                <div>
                  <span>Verification Code</span>
                  <strong>{certificate.verificationCode}</strong>
                </div>
              </div>
            </section>

            <section className="certificate-image-card">
              <img src={certificate.certificateImageUrl} alt={`${certificate.name} certificate`} />
              <div className="certificate-actions">
                <button
                  className="download-button"
                  onClick={() => downloadImage(certificate.certificateImageUrl, `${certificate.name}-certificate.jpg`)}
                >
                  <FiDownload /> Download Certificate
                </button>
                <button className="share-button" onClick={handleShare}>
                  <FiShare2 /> Share Certificate URL
                </button>
              </div>
              {shareFeedback && <p className="share-feedback">{shareFeedback}</p>}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CertificateVerificationPage;
